//@flow
import {switchMap, map, debounceTime} from 'rxjs/operators';
import {ActionsObservable, ofType} from 'redux-observable';

import {getDevices, getRavenAccount, defaultPagination} from './api';
import {
  createReducer,
  ActionCreator,
  buildCatchError,
} from '../../../store/utils';

import {arrayToOrderedMap, orderedMapToArray} from '../../../utils/array';

import type {GetDevicesResponsePagination} from './api';

import type {
  Conversation,
  ConversationTag,
  Pagination,
  ConversationState,
  StoreDevice,
  ConversationMessage,
  ReactionMessage,
  ChatServiceType,
  MessageScheduleState,
} from '../../../entities';

export type ConversationFilters = {
  pagination?: Pagination,
  tags?: string[],
  state: ConversationState,
  identifier?: string,
  admin?: boolean,
  collaborators?: string[],
  scheduleMessageStates?: MessageScheduleState[],
  requiresAssistance?: boolean,
  services: string[],
  deviceId?: string,
};

export type ConversationCountCollaborator = {
  id: string,
  count: number,
};
export type ConversationCount = {
  total: number,
  admin: number,
  requiresAssistanceCount?: number,
  collaborators: ConversationCountCollaborator[],
};

export type ConversationCountAction = {
  total?: number,
  admin?: number,
  collaborators?: ConversationCountCollaborator[],
};

export type MultiChatState = {
  storeDevices: StoreDevice[],
  ravenAccount?: boolean,
  isFetchingStoreDevices?: boolean,
  isFetchingRavenAccount?: boolean,
  currentConversation?: Conversation,
  conversationTags: ConversationTag[],
  conversations: Conversation[],
  conversationPagination?: Pagination,
  isFetchingConversations?: boolean,
  conversationFilters: ConversationFilters,
  conversationMessage: ConversationMessage[],
  reactionMessages: ReactionMessage[],
  conversationCount: ConversationCount,
  selectedConversations: Conversation[],
  isAtChatEnd: boolean,
  isSynchronizingConversations: boolean,
  nextTokenArchived?: string,
  prevTokenArchived?: string,
  conversationId?: string,
  deviceReconnect: {
    qrCode: string,
    phone: string,
  },
};

export type MultiChatAction = {
  devices?: StoreDevice[],
  chatService?: ChatServiceType[],
  serviceType?: ChatServiceType,
  conversation?: Conversation,
  conversationTags?: ConversationTag[],
  conversationTag?: ConversationTag,
  conversationTagsId?: string[],
  conversations?: Conversation[],
  conversationId?: string,
  conversationIds?: string[],
  tags?: ConversationTag[],
  isSynchronizingConversations?: boolean,
  nextTokenArchived?: string,
  prevTokenArchived?: string,
  isAtChatEnd?: boolean,
  crmConversationState?: ConversationState,
  unread?: boolean,

  conversationFilterTag?: string[],
  conversationFilterState?: ConversationState,
  conversationFilterPagination?: Pagination,
  conversationFilterIdentifier?: string,
  conversationFilterAdmin?: boolean,
  conversationFilterCollaborators?: string[],
  conversationFilterScheduleMessageStates?: MessageScheduleState[],
  conversationFilterServices?: string[],
  conversationFilterDeviceId?: string,
  conversationFilterRequiresAssistance?: boolean,

  deviceId?: string,
  conversationPagination?: Pagination,
  messages?: ConversationMessage[],
  message?: ConversationMessage,

  reactionMessage?: ReactionMessage,
  reactionMessages?: ReactionMessage[],
  reactionMessageId?: string,
  conversationCount?: ConversationCountAction,
  totalConversationCount?: number,
  adminConversationCount?: number,
  collaboratorConversationCount?: ConversationCountCollaborator,
  qrCode?: string,
};

export const initialState: MultiChatState = {
  storeDevices: [],
  conversationTags: [],
  conversations: [],
  conversationFilters: {
    state: 'ALL',
    services: ['WHATSAPP_WEB', 'INSTAGRAM', 'FB_MESSENGER', 'WHATSAPP_API'],
  },
  conversationMessage: [],
  conversationCount: {
    total: 0,
    admin: 0,
    collaborators: [],
  },
  selectedConversations: [],
  reactionMessages: [],
  isSynchronizingConversations: true,
  nextTokenArchived: '',
  conversationId: '',
  deviceReconnect: {
    qrCode: '',
    phone: '',
  },
  isAtChatEnd: true,
};

export class FetchStoreDevices extends ActionCreator<MultiChatAction> {}
export class FetchStoreDevicesSuccess extends ActionCreator<MultiChatAction> {}
export class FetchStoreDevicesFailure extends ActionCreator<MultiChatAction> {}

export class DeleteStoreDevice extends ActionCreator<MultiChatAction> {}

export class ReconnectStoreDevice extends ActionCreator<MultiChatAction> {}
export class ResetReconnectStoreDevice extends ActionCreator<MultiChatAction> {}

export class GetRavenAccount extends ActionCreator<MultiChatAction> {}
export class GetRavenAccountSuccess extends ActionCreator<MultiChatAction> {}
export class GetRavenAccountFailure extends ActionCreator<MultiChatAction> {}

export class SetStoreConversations extends ActionCreator<MultiChatAction> {}
export class UpdateStoreConversation extends ActionCreator<MultiChatAction> {}
export class UpdateStoreConversationTags extends ActionCreator<MultiChatAction> {}

export class SetFilterConversation extends ActionCreator<MultiChatAction> {}
export class ClearFilterConversation extends ActionCreator<MultiChatAction> {}

export class SetCurrentConversation extends ActionCreator<MultiChatAction> {}
export class ClearCurrentConversation extends ActionCreator<MultiChatAction> {}
export class UpdateCurrentConversation extends ActionCreator<MultiChatAction> {}
export class AddConversationPagination extends ActionCreator<MultiChatAction> {}

export class SetConversationTags extends ActionCreator<MultiChatAction> {}
export class AddConversationTag extends ActionCreator<MultiChatAction> {}
export class EditConversationTag extends ActionCreator<MultiChatAction> {}
export class DeleteConversationTag extends ActionCreator<MultiChatAction> {}

export class SetConversationMessages extends ActionCreator<MultiChatAction> {}
export class AddNewMessages extends ActionCreator<MultiChatAction> {}
export class AddOldMessages extends ActionCreator<MultiChatAction> {}
export class AddArchivedMessages extends ActionCreator<MultiChatAction> {}

export class SetConversationReactionMessages extends ActionCreator<MultiChatAction> {}
export class AddConversationReactionMessage extends ActionCreator<MultiChatAction> {}
export class RemoveConversationReactionMessage extends ActionCreator<MultiChatAction> {}

export class SetConversationCount extends ActionCreator<MultiChatAction> {}
export class SetTotalConversationCount extends ActionCreator<MultiChatAction> {}
export class SetAdminConversationCount extends ActionCreator<MultiChatAction> {}
export class SetCollaboratorConversationCount extends ActionCreator<MultiChatAction> {}

export class TogglerConversationSelection extends ActionCreator<MultiChatAction> {}
export class SetConversationSelection extends ActionCreator<MultiChatAction> {}
export class SetUnreadConversation extends ActionCreator<MultiChatAction> {}
export class SetUnreadMultipleConversation extends ActionCreator<MultiChatAction> {}
export class SetMultipleConversationState extends ActionCreator<MultiChatAction> {}

export class SetConversationSyncronizing extends ActionCreator<MultiChatAction> {}

export class SetNextTokenArchived extends ActionCreator<MultiChatAction> {}
export class ResetNextTokenArchived extends ActionCreator<MultiChatAction> {}
export class SetPrevTokenArchived extends ActionCreator<MultiChatAction> {}
export class ResetPrevTokenArchived extends ActionCreator<MultiChatAction> {}

export class SetIdConversation extends ActionCreator<MultiChatAction> {}
export class ResetIdConversation extends ActionCreator<MultiChatAction> {}

export class ToggleRequiredAssistance extends ActionCreator<MultiChatAction> {}

export class SetIsAtChatEnd extends ActionCreator<MultiChatAction> {}

export const actions = {
  getRavenAccount: () => new GetRavenAccount(),
  getStoreDevices: (service: ChatServiceType[]) =>
    new FetchStoreDevices({
      chatService: service,
    }),

  setStoreConversations: (
    conversations: Conversation[],
    pagination: Pagination
  ) =>
    new SetStoreConversations({
      conversations,
      conversationPagination: pagination,
    }),
  updateStoreConversation: (conversation: Conversation) =>
    new UpdateStoreConversation({
      conversation,
    }),
  updateStoreConversationTags: (
    conversationId: string,
    tags: ConversationTag[]
  ) =>
    new UpdateStoreConversationTags({
      conversationId,
      tags,
    }),

  setCurrentConversation: (conversation: Conversation) =>
    new SetCurrentConversation({
      conversation,
    }),

  updateCurrentConversation: (conversation: Conversation) =>
    new UpdateCurrentConversation({
      conversation,
    }),
  clearCurrentConversation: () => new ClearCurrentConversation(),

  setConversationTags: (conversationTags: ConversationTag[]) =>
    new SetConversationTags({
      conversationTags,
    }),
  addConversationTag: (conversationTags: ConversationTag[]) =>
    new AddConversationTag({
      conversationTags,
    }),
  editConversationTag: (conversationTag: ConversationTag) =>
    new EditConversationTag({
      conversationTag,
    }),

  deleteConversationTag: (conversationTagsId: string[]) =>
    new DeleteConversationTag({
      conversationTagsId,
    }),

  setTagsConversationFilter: (tags: string[]) =>
    new SetFilterConversation({
      conversationFilterTag: tags,
    }),
  setPaginationConversationFilter: (pagination: Pagination) =>
    new SetFilterConversation({
      conversationFilterPagination: pagination,
    }),
  setStateConversationFilter: (state: ConversationState) =>
    new SetFilterConversation({
      conversationFilterState: state,
    }),

  setIdentifierConversationFilter: (identifier: string) =>
    new SetFilterConversation({
      conversationFilterIdentifier: identifier,
    }),

  setAdminConversationFilter: (admin: boolean) =>
    new SetFilterConversation({
      conversationFilterAdmin: admin,
    }),

  setRequiresAssistanceConversationFilter: (requiresAssistance: boolean) =>
    new SetFilterConversation({
      conversationFilterRequiresAssistance: requiresAssistance,
    }),

  setCollaboratorsConversationFilter: (collaborators: string[]) =>
    new SetFilterConversation({
      conversationFilterCollaborators: collaborators,
    }),

  setServicesConversationFilter: (
    services: ChatServiceType[],
    deviceId?: ?string
  ) =>
    new SetFilterConversation({
      conversationFilterServices: services,
      conversationFilterDeviceId: deviceId || '',
    }),

  setDeviceIdConversationFilter: (deviceId: ?string) =>
    new SetFilterConversation({
      conversationFilterDeviceId: deviceId || '',
    }),

  setScheduleMessageStateConversationFilter: (
    scheduleMessageStates: MessageScheduleState[]
  ) =>
    new SetFilterConversation({
      conversationFilterScheduleMessageStates: scheduleMessageStates,
    }),

  setConversationSyncronizing: (isSynchronizingConversations: boolean) =>
    new SetConversationSyncronizing({
      isSynchronizingConversations,
    }),
  setNextTokenArchived: (prevToken: string) =>
    new SetNextTokenArchived({
      nextTokenArchived: prevToken,
    }),
  resetNextTokenArchived: () => new ResetNextTokenArchived(),

  setPrevTokenArchived: (prevToken: string) =>
    new SetPrevTokenArchived({
      prevTokenArchived: prevToken,
    }),

  resetPrevTokenArchived: () => new ResetPrevTokenArchived(),

  setIdConversation: (conversationId: string) =>
    new SetIdConversation({
      conversationId,
    }),
  resetIdConversation: () => new ResetIdConversation(),

  clearFilterConversation: () => new ClearFilterConversation(),

  deleteStoreDevice: (deviceId: string, serviceType: ChatServiceType) =>
    new DeleteStoreDevice({
      deviceId,
      serviceType,
    }),
  reconnectStoreDevice: (
    deviceId: string,
    serviceType: ChatServiceType,
    qrCode: string
  ) =>
    new ReconnectStoreDevice({
      deviceId,
      serviceType,
      qrCode,
    }),
  resetReconnectStoreDevice: () => new ResetReconnectStoreDevice(),
  addConversationPagination: (
    conversations: Conversation[],
    pagination: Pagination
  ) =>
    new AddConversationPagination({
      conversations,
      conversationPagination: pagination,
    }),
  setConversationMessages: (messages: ConversationMessage[]) =>
    new SetConversationMessages({
      messages,
    }),

  setConversationReactionMessages: (reactionMessages: ReactionMessage[]) =>
    new SetConversationReactionMessages({
      reactionMessages,
    }),

  addConversationReactionMessage: (reactionMessage: ReactionMessage) =>
    new AddConversationReactionMessage({
      reactionMessage,
    }),

  removeConversationReactionMessage: (reactionMessageId: string) =>
    new RemoveConversationReactionMessage({
      reactionMessageId,
    }),

  addNewMessages: (messages: ConversationMessage[]) =>
    new AddNewMessages({
      messages,
    }),
  addOldMessages: (messages: ConversationMessage[]) =>
    new AddOldMessages({
      messages,
    }),
  addArchivedMessages: (messages: ConversationMessage[]) =>
    new AddArchivedMessages({
      messages,
    }),

  setConversationCount: (conversationCount: ConversationCountAction) =>
    new SetConversationCount({
      conversationCount,
    }),
  setTotalConversationCount: (totalConversationCount: number) =>
    new SetTotalConversationCount({
      totalConversationCount,
    }),
  setAdminConversationCount: (adminConversationCount: number) =>
    new SetAdminConversationCount({
      adminConversationCount,
    }),

  setCollaboratorConversationCount: (collaboratorId: string, count: number) =>
    new SetCollaboratorConversationCount({
      collaboratorConversationCount: {
        id: collaboratorId,
        count,
      },
    }),

  clearConversationSelection: () =>
    new SetConversationSelection({
      conversations: [],
    }),

  setConversationSelection: (conversations: Conversation[]) =>
    new SetConversationSelection({
      conversations,
    }),

  togglerConversationSelection: (conversation: Conversation) =>
    new TogglerConversationSelection({
      conversation,
    }),

  setUnreadConversation: (conversationId: string, unread: boolean) =>
    new SetUnreadConversation({
      conversationId,
      unread,
    }),

  setUnreadMultipleConversation: (conversationIds: string[], unread: boolean) =>
    new SetUnreadMultipleConversation({
      conversationIds,
      unread,
    }),
  setMultipleConversationState: (
    conversationIds: string[],
    state: ConversationState
  ) =>
    new SetMultipleConversationState({
      conversationIds,
      crmConversationState: state,
    }),

  toggleRequiredAssistance: (conversationId: string) =>
    new ToggleRequiredAssistance({
      conversationId,
    }),

  setIsAtChatEnd: (isAtChatEnd: boolean) =>
    new SetIsAtChatEnd({
      isAtChatEnd,
    }),
};

export default createReducer<MultiChatState>(initialState, {
  [FetchStoreDevices.type]: (state, action) => ({
    ...state,
    isFetchingStoreDevices: true,
  }),
  [FetchStoreDevicesSuccess.type]: (state, action) => ({
    ...state,
    isFetchingStoreDevices: false,
    storeDevices: action.devices?.filter(device => device.state !== 'ARCHIVED'),
  }),
  [FetchStoreDevicesFailure.type]: (state, action) => ({
    ...state,
    isFetchingStoreDevices: false,
  }),
  [GetRavenAccount.type]: (state, action) => ({
    ...state,
    isFetchingRavenAccount: true,
  }),
  [GetRavenAccountSuccess.type]: (state, action) => ({
    ...state,
    isFetchingRavenAccount: false,
    ravenAccount: true,
  }),
  [GetRavenAccountFailure.type]: (state, action) => ({
    ...state,
    isFetchingRavenAccount: false,
    ravenAccount: false,
  }),
  [SetCurrentConversation.type]: (state, action) => ({
    ...state,
    currentConversation: action.conversation,
  }),
  [UpdateCurrentConversation.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ) => {
    const {conversation} = action;

    if (!conversation) return state;

    return {
      ...state,
      currentConversation: {
        ...state.currentConversation,
        ...conversation,
      },
    };
  },
  [ClearCurrentConversation.type]: (state, action) => ({
    ...state,
    currentConversation: undefined,
  }),
  [SetStoreConversations.type]: (state, action) => {
    return {
      ...state,
      conversations: action.conversations,
      conversationPagination: action.conversationPagination,
    };
  },
  [UpdateStoreConversation.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const patchConversation: ?Conversation = action.conversation;
    if (!patchConversation) return state;

    const {
      state: stateFilter,
      services: serviceFilter,
    } = state.conversationFilters;

    const conversationMap = arrayToOrderedMap(state.conversations, 'id');
    const previousConversation = conversationMap.get(patchConversation.id);

    const mergedConversation: Conversation = previousConversation
      ? {
          ...previousConversation,
          ...patchConversation,
          identifier: previousConversation.identifier,
          deviceIdentifier: previousConversation.deviceIdentifier,
        }
      : patchConversation;

    const passesServiceFilter = serviceFilter.includes(
      mergedConversation.ravenService
    );
    const passesStateFilter =
      stateFilter === 'ALL' ||
      mergedConversation.crmConversationState === stateFilter;

    let nextConversationMap = conversationMap.delete(mergedConversation.id);
    if (passesServiceFilter && passesStateFilter) {
      nextConversationMap = nextConversationMap.set(
        mergedConversation.id,
        mergedConversation
      );
    }

    const sortedConversationMap = nextConversationMap.sort((a, b) => {
      const aPinned = Boolean(a.pinned);
      const bPinned = Boolean(b.pinned);

      if (aPinned !== bPinned) {
        return aPinned ? -1 : 1;
      }

      return a.lastMessageAt < b.lastMessageAt ? 1 : -1;
    });
    const conversationsList = orderedMapToArray(sortedConversationMap);

    const currentConversationId =
      state.currentConversation != null ? state.currentConversation.id : null;
    const currentConversationExists =
      currentConversationId != null &&
      nextConversationMap.has(currentConversationId);

    let currentConversation = state.currentConversation;

    if (!currentConversationExists) {
      currentConversation =
        conversationsList.length > 0 ? conversationsList[0] : null;
    } else if (currentConversationId === mergedConversation.id) {
      currentConversation = mergedConversation;
    }

    return {
      ...state,
      conversations: conversationsList,
      currentConversation,
    };
  },
  [UpdateStoreConversationTags.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const isCurrentConversation =
      state.currentConversation?.id === action.conversationId;

    const newTags = action.tags || [];
    let currentConversation = state.currentConversation;
    if (isCurrentConversation) {
      currentConversation = {
        ...currentConversation,
        tags: newTags,
      };
    }

    return {
      ...state,
      conversations: state.conversations.map(conversation => {
        if (conversation.id === action.conversationId) {
          return {
            ...conversation,
            tags: newTags,
          };
        }
        return conversation;
      }),
      //$FlowFixMe
      currentConversation,
    };
  },

  [SetConversationTags.type]: (state, action) => ({
    ...state,
    conversationTags: action.conversationTags,
  }),
  [AddConversationTag.type]: (state, action) => ({
    ...state,
    conversationTags: [...state.conversationTags, ...action.conversationTags],
  }),
  [EditConversationTag.type]: (state, action) => ({
    ...state,
    conversationTags: state.conversationTags.map(conversationTag => {
      if (conversationTag.id === action.conversationTag.id) {
        return action.conversationTag;
      }

      return conversationTag;
    }),
  }),
  [DeleteConversationTag.type]: (state, action) => ({
    ...state,
    conversationTags: state.conversationTags.filter(
      conversationTag => !action.conversationTagsId.includes(conversationTag.id)
    ),
  }),
  [SetFilterConversation.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const newState = {...state};

    if (action.conversationFilterTag) {
      newState.conversationFilters.tags = action.conversationFilterTag;
    }
    if (action.conversationFilterPagination) {
      newState.conversationFilters.pagination =
        action.conversationFilterPagination;
    }
    if (action.conversationFilterState) {
      newState.conversationFilters.state = action.conversationFilterState;
    }

    if (typeof action.conversationFilterIdentifier !== 'undefined') {
      newState.conversationFilters.identifier =
        action.conversationFilterIdentifier;
    }

    if (typeof action.conversationFilterAdmin !== 'undefined') {
      if (action.conversationFilterAdmin)
        newState.conversationFilters.collaborators = [];
      newState.conversationFilters.admin = action.conversationFilterAdmin;
    }

    if (typeof action.conversationFilterScheduleMessageStates !== 'undefined') {
      newState.conversationFilters.scheduleMessageStates =
        action.conversationFilterScheduleMessageStates;
    }

    if (action.conversationFilterCollaborators) {
      if (
        action.conversationFilterCollaborators &&
        action.conversationFilterCollaborators.length > 0
      )
        newState.conversationFilters.admin = false;

      newState.conversationFilters.collaborators =
        action.conversationFilterCollaborators;
    }

    if (action.conversationFilterServices) {
      const empty = action.conversationFilterServices.length === 0;

      newState.conversationFilters.services = empty
        ? ['WHATSAPP_WEB', 'INSTAGRAM', 'FB_MESSENGER', 'WHATSAPP_API']
        : action.conversationFilterServices;

      // Multi-service / "all" filters cannot be paired with deviceId
      if (empty || action.conversationFilterServices.length !== 1) {
        newState.conversationFilters.deviceId = undefined;
      }
    }

    if (typeof action.conversationFilterDeviceId !== 'undefined') {
      newState.conversationFilters.deviceId =
        action.conversationFilterDeviceId || undefined;
    }

    if (typeof action.conversationFilterRequiresAssistance !== 'undefined') {
      newState.conversationFilters.requiresAssistance =
        action.conversationFilterRequiresAssistance;
    }

    return newState;
  },
  [DeleteStoreDevice.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ) => {
    const {deviceId, serviceType} = action;

    if (!deviceId || !serviceType) return state;

    return {
      ...state,
      storeDevices: state.storeDevices.filter(device => {
        return `${device.id}${device.service}` !== `${deviceId}${serviceType}`;
      }),
    };
  },
  [ReconnectStoreDevice.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ) => {
    const {deviceId, serviceType, qrCode} = action;

    if (!deviceId || !serviceType) return state;

    const device = state.storeDevices.find(
      device => device.id === deviceId && device.service === serviceType
    );

    return {
      ...state,
      deviceReconnect: {
        qrCode: qrCode,
        phone: device?.identifier || '',
      },
    };
  },
  [ResetReconnectStoreDevice.type]: (state: MultiChatState) => ({
    ...state,
    deviceReconnect: {
      qrCode: '',
      phone: '',
    },
  }),
  [AddConversationPagination.type]: (state, action) => ({
    ...state,
    conversations: [...state.conversations, ...action.conversations],
    conversationPagination: action.conversationPagination,
  }),
  [SetConversationMessages.type]: (state, action) => ({
    ...state,
    conversationMessage: action.messages,
  }),
  [AddNewMessages.type]: (state: MultiChatState, action: MultiChatAction) => {
    const prevMessagesMap = state.conversationMessage;
    const newMessagesMap = action.messages;

    if (!newMessagesMap) return state;

    const combinedMessages = [...newMessagesMap, ...prevMessagesMap];

    const orderedMap = arrayToOrderedMap(combinedMessages);

    const sortedOrderedMap = orderedMap.sortBy(msg => msg.receivedAt || 0);
    const conversationMessage = orderedMapToArray(sortedOrderedMap).reverse();

    return {
      ...state,
      conversationMessage,
    };
  },
  [AddOldMessages.type]: (state: MultiChatState, action: MultiChatAction) => {
    const prevMessagesMap = state.conversationMessage;
    const newMessagesMap = action.messages;

    // $FlowFixMe
    const conversationMessage = [...prevMessagesMap, ...newMessagesMap];

    return {
      ...state,
      conversationMessage,
    };
  },
  [AddArchivedMessages.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ) => {
    const prevMessagesMap = state.conversationMessage;

    const newMessagesMap = action.messages;

    // $FlowFixMe
    const conversationMessage = [...prevMessagesMap, ...newMessagesMap];

    return {
      ...state,
      conversationMessage,
    };
  },

  [SetConversationCount.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ) => {
    return {
      ...state,
      conversationCount: {
        ...state.conversationCount,
        ...action.conversationCount,
      },
    };
  },
  [SetTotalConversationCount.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const total = isNaN(action.totalConversationCount)
      ? state.conversationCount.total
      : action.totalConversationCount || 0;
    return {
      ...state,
      conversationCount: {
        ...state.conversationCount,
        total,
      },
    };
  },
  [SetAdminConversationCount.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const admin = isNaN(action.adminConversationCount)
      ? state.conversationCount.admin
      : action.adminConversationCount || 0;
    return {
      ...state,
      conversationCount: {
        ...state.conversationCount,
        admin,
      },
    };
  },
  [SetCollaboratorConversationCount.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const {collaboratorConversationCount} = action;

    if (!collaboratorConversationCount) return state;

    const exist = state.conversationCount.collaborators.find(collaborator => {
      return collaborator.id === collaboratorConversationCount.id;
    });

    if (!exist) {
      const collaborators = [
        ...state.conversationCount.collaborators,
        collaboratorConversationCount,
      ];

      return {
        ...state,
        conversationCount: {
          ...state.conversationCount,
          collaborators,
        },
      };
    }

    return {
      ...state,
      conversationCount: {
        ...state.conversationCount,
        collaborators: state.conversationCount.collaborators.map(
          collaborator => {
            if (collaborator.id === collaboratorConversationCount.id) {
              return collaboratorConversationCount;
            }
            return collaborator;
          }
        ),
      },
    };
  },

  [ClearFilterConversation.type]: (state: MultiChatState): MultiChatState => ({
    ...state,
    conversationFilters: {
      state: 'ALL',
      services: ['WHATSAPP_WEB', 'INSTAGRAM', 'FB_MESSENGER', 'WHATSAPP_API'],
    },
  }),
  [SetConversationSelection.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    return {
      ...state,
      selectedConversations: action.conversations || [],
    };
  },
  [TogglerConversationSelection.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const {conversation} = action;
    const {selectedConversations} = state;
    if (!conversation) return state;

    const exist = state.selectedConversations.find(
      selectedConversation => selectedConversation.id === conversation.id
    );

    if (!exist) {
      return {
        ...state,
        selectedConversations: [...selectedConversations, conversation],
      };
    }

    return {
      ...state,
      selectedConversations: selectedConversations.filter(
        selectedConversation => selectedConversation.id !== conversation.id
      ),
    };
  },
  [SetUnreadConversation.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const {conversationId, unread} = action;

    if (!conversationId) return state;

    const conversationsMap = arrayToOrderedMap(state.conversations, 'id');

    const prevConversation = conversationsMap.get(conversationId);

    if (!prevConversation) return state;

    const newConversation: Conversation = {
      ...prevConversation,
      read: !unread,
    };
    const updatedConversation = conversationsMap.set(
      conversationId,
      newConversation
    );

    const conversations = orderedMapToArray(updatedConversation);

    return {
      ...state,
      conversations,
    };
  },
  [SetUnreadMultipleConversation.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const {conversationIds, unread} = action;

    if (!conversationIds) return state;

    const newConversations = state.conversations.map(conversation => {
      if (conversationIds.includes(conversation.id)) {
        return {
          ...conversation,
          read: !unread,
        };
      }
      return conversation;
    });

    return {
      ...state,
      conversations: newConversations,
      currentConversation: undefined,
    };
  },

  [SetMultipleConversationState.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const {conversationIds, crmConversationState} = action;

    if (!conversationIds || !crmConversationState) return state;

    const filterState = state.conversationFilters.state;

    const newConversations: Conversation[] = state.conversations.map(
      conversation => {
        if (conversationIds.includes(conversation.id)) {
          return {
            ...conversation,
            crmConversationState,
          };
        }
        return conversation;
      }
    );

    if (filterState === 'ALL') {
      return {
        ...state,
        conversations: newConversations,
      };
    }

    const currentConversationUpdated = newConversations.find(
      conversation => conversation.id === state.currentConversation?.id
    );

    if (currentConversationUpdated?.crmConversationState === filterState) {
      return {
        ...state,
        conversations: newConversations,
        currentConversation: currentConversationUpdated,
      };
    }

    const newCurrentConversation = newConversations.find(
      conversation => conversation.crmConversationState === filterState
    );

    return {
      ...state,
      conversations: newConversations,
      currentConversation: newCurrentConversation,
    };
  },
  [SetConversationSyncronizing.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    if (action.isSynchronizingConversations === undefined) return state;
    return {
      ...state,
      isSynchronizingConversations: action.isSynchronizingConversations,
    };
  },
  [SetNextTokenArchived.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    if (!action.nextTokenArchived) return state;

    return {
      ...state,
      nextTokenArchived: action.nextTokenArchived,
    };
  },
  [ResetNextTokenArchived.type]: (state: MultiChatState): MultiChatState => ({
    ...state,
    nextTokenArchived: '',
  }),
  [SetPrevTokenArchived.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    if (!action.prevTokenArchived) return state;
    return {
      ...state,
      prevTokenArchived: action.prevTokenArchived,
    };
  },
  [ResetPrevTokenArchived.type]: (state: MultiChatState): MultiChatState => ({
    ...state,
    prevTokenArchived: '',
  }),

  [SetIdConversation.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    if (!action.conversationId) return state;

    return {
      ...state,
      conversationId: action.conversationId,
    };
  },
  [ResetIdConversation.type]: (state: MultiChatState): MultiChatState => ({
    ...state,
    conversationId: '',
  }),
  [ToggleRequiredAssistance.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const {conversationId} = action;
    if (!conversationId) return state;

    const conversationsMap = arrayToOrderedMap(state.conversations, 'id');
    const prevConversation = conversationsMap.get(conversationId);

    if (!prevConversation) return state;

    const newConversation: Conversation = {
      ...prevConversation,
      requiresAssistance: !prevConversation.requiresAssistance,
    };
    const updatedConversation = conversationsMap.set(
      conversationId,
      newConversation
    );
    const conversations = orderedMapToArray(updatedConversation);
    const currentConversation =
      state.currentConversation?.id === conversationId
        ? newConversation
        : state.currentConversation;

    return {
      ...state,
      conversations,
      currentConversation,
    };
  },
  [SetConversationReactionMessages.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    return {
      ...state,
      reactionMessages: action.reactionMessages || [],
    };
  },

  [AddConversationReactionMessage.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    if (!action.reactionMessage) return state;
    const reactionMessages = [...state.reactionMessages];
    reactionMessages.push(action.reactionMessage);
    return {
      ...state,
      reactionMessages,
    };
  },

  [RemoveConversationReactionMessage.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    const reactionMessages = state.reactionMessages.filter(
      reactionMessage => reactionMessage.id !== action.reactionMessageId
    );
    return {
      ...state,
      reactionMessages,
    };
  },
  [SetIsAtChatEnd.type]: (
    state: MultiChatState,
    action: MultiChatAction
  ): MultiChatState => {
    return {
      ...state,
      isAtChatEnd: !!action.isAtChatEnd,
    };
  },
});

export function getRavenAccountEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(GetRavenAccount.type),
    debounceTime(500),
    switchMap(() => {
      return getRavenAccount().pipe(
        map(() => new GetRavenAccountSuccess()),
        buildCatchError(GetRavenAccountFailure)
      );
    })
  );
}

export function fetchStoreDevicesEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchStoreDevices.type),
    debounceTime(500),
    switchMap(({chatService}: MultiChatAction) => {
      const pagination = defaultPagination;

      const services = chatService ? chatService.join(',') : '';

      return getDevices({
        services,
        size: pagination.size.toString(),
        page: pagination.page.toString(),
      }).pipe(
        map(
          (devices: GetDevicesResponsePagination) =>
            new FetchStoreDevicesSuccess({
              devices: devices.data,
            })
        ),
        buildCatchError(FetchStoreDevicesFailure)
      );
    })
  );
}
